/**
 * Defiant Yarn — AJAX add to cart for the Defiant pre-order section.
 *
 * The section uses a plain {% form 'product' %}, which would post and navigate to
 * /cart. On a 34-colourway pre-order page that ends the shopping trip after one
 * skein, so this intercepts the submit and adds through /cart/add.js instead.
 *
 * WHY THIS IS A MODULE NOW
 *   Horizon is entirely event driven. Nothing in the theme watches the network:
 *   the cart icon, the cart drawer and every cart-items-component sit waiting for
 *   a CartLinesUpdateEvent and do their work inside its promise. The old version
 *   of this file poked the cart bubble by hand and told nobody else, which is why
 *   the drawer kept showing a stale cart until the page was refreshed.
 *
 *   So: dispatch the real event, ask /cart/add.js for the re-rendered cart
 *   sections, and resolve the promise with what the listeners expect. The drawer,
 *   the icon, the quantity selectors and the free shipping bar all follow.
 *
 * If this module fails to load, the form falls back to a normal POST and the
 * shopper lands on /cart. Worse, but never broken.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const RESET_DELAY = 1800;

function routes() {
  return (window.Theme && window.Theme.routes) || {};
}

/** Shopify hands these out with and without the .js suffix depending on version. */
function asJson(url, fallback) {
  const value = url || fallback;
  return /\.js$/.test(value) ? value : value + '.js';
}

const addUrl = () => asJson(routes().cart_add_url, '/cart/add');
const cartUrl = () => asJson(routes().cart_url, '/cart');

/** Every cart-items-component that wants re-rendering after the add. */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

function setMessage(el, text, isError) {
  if (!el) return;
  el.textContent = text;
  el.classList.toggle('df-cart-msg--error', Boolean(isError));
  el.hidden = !text;
}

function openDrawer() {
  const actions = window.Shopify && window.Shopify.actions;
  if (actions && typeof actions.openCart === 'function') {
    actions.openCart();
    return;
  }
  const opener = document.querySelector('.header-actions__cart-icon');
  if (opener && typeof opener.click === 'function') opener.click();
}

function bind(form) {
  if (form.dataset.dfCartBound === 'true') return;
  form.dataset.dfCartBound = 'true';

  const wrap = form.closest('[data-df-open-drawer]');
  const openAfterAdd = Boolean(wrap) && wrap.getAttribute('data-df-open-drawer') === 'true';

  form.addEventListener('submit', async (event) => {
    const button = form.querySelector('[name="add"]');
    // Something else (a pre-order app) may have taken the button over.
    if (button && button.disabled) return;

    event.preventDefault();

    const message = form.querySelector('[data-df-cart-msg]');
    const label = button ? button.querySelector('span') : null;
    const originalLabel = label ? label.innerHTML : null;

    if (button) button.disabled = true;
    setMessage(message, '', false);

    const body = new FormData(form);
    const sectionIds = cartSectionIds();
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    const variantId = String(body.get('id') || '');
    const quantity = parseInt(String(body.get('quantity') || '1'), 10) || 1;

    // Dispatched before the fetch: the drawer reads event.action synchronously to
    // decide whether to auto-open, then waits on the promise for the contents.
    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(addUrl(), {
        method: 'POST',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        body,
        credentials: 'same-origin',
      });
      const added = await response.json();

      if (!response.ok) {
        throw new Error(added.description || added.message || 'Could not add that to your cart.');
      }

      // The theme does not trust the add response for totals, so neither do we.
      const cart = await fetch(cartUrl(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'df-preorder-cart',
          sourceId: form.id || 'df-product-form',
          itemCount: cart.item_count,
          productId: form.dataset.dfProductId,
          sections: added.sections,
          didError: false,
        },
      });

      if (label) label.innerHTML = 'Added &nbsp; &#10003;';
      setMessage(
        message,
        cart.item_count === 1 ? '1 item in your bag. Keep going.' : cart.item_count + ' items in your bag. Keep going.',
        false
      );
      if (openAfterAdd) openDrawer();
    } catch (error) {
      // Resolve rather than reject: a rejected promise leaves every listener
      // hanging and the cart UI stale for the rest of the session.
      deferred.resolve({
        cart: null,
        detail: { source: 'df-preorder-cart', sourceId: form.id || 'df-product-form', didError: true },
      });
      setMessage(message, (error && error.message) || 'Could not add that to your cart.', true);
    } finally {
      window.setTimeout(() => {
        if (button) button.disabled = false;
        if (label && originalLabel !== null) label.innerHTML = originalLabel;
      }, RESET_DELAY);
    }
  });
}

/**
 * The photo disclaimer that sits under the About Me card.
 *
 * This belongs in sections/PlushCorePreorder.liquid as a single
 * {% render 'df-photo-note' %} below the About Me article. It lives here instead
 * because editing a section means re-uploading the whole 60KB file, and this
 * asset is already loaded by exactly that section. snippets/df-photo-note.liquid
 * holds the same copy and markup: move the render call into the section when
 * there is a code-editor session handy, then delete photoNote/photoNoteStyles.
 *
 * The note mirrors the card's hidden state, because with colourway profiles on
 * the card starts hidden and the profile JS unhides it on the first colour.
 */
const PHOTO_NOTE =
  'all photos taken with iPhone 17 pro, white balanced only. full skein photos will be uploaded and replaced when we receive the full skeins';

function photoNoteStyles() {
  if (document.getElementById('df-photo-note-styles')) return;
  const style = document.createElement('style');
  style.id = 'df-photo-note-styles';
  style.textContent =
    '.df-photo-note{margin:10px 2px 0;font:400 10px/1.6 "JetBrains Mono",monospace;letter-spacing:.5px;' +
    'color:var(--df-fg-muted,#8a8a94);max-width:52ch}' +
    '@media (max-width:900px){.df-photo-note{max-width:none}}';
  document.head.appendChild(style);
}

function photoNote() {
  document.querySelectorAll('[data-df-about-card]').forEach((card) => {
    const next = card.nextElementSibling;
    if (next && next.classList.contains('df-photo-note')) return;

    const note = document.createElement('p');
    note.className = 'df-photo-note';
    note.textContent = PHOTO_NOTE;
    note.hidden = card.hidden;
    card.insertAdjacentElement('afterend', note);

    new MutationObserver(() => {
      note.hidden = card.hidden;
    }).observe(card, { attributes: true, attributeFilter: ['hidden'] });
  });
}

function scan() {
  document.querySelectorAll('form.df-form').forEach(bind);
  photoNoteStyles();
  photoNote();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scan);
} else {
  scan();
}

// Theme editor re-renders the section without a page load.
document.addEventListener('shopify:section:load', scan);
